import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';

// Palavras-chave para detectar criação de documentos (mais rigorosas)
const DOCUMENT_KEYWORDS = [
  'petição', 'peticao', 'contrato', 'parecer', 'recurso', 'contestação', 'contestacao',
  'procuração', 'procuracao', 'acordo', 'memorial', 'defesa', 'alegações', 'alegacoes',
  'mandado', 'notificação', 'notificacao', 'intimação', 'intimacao', 'apelação', 'apelacao',
  'embargos', 'agravo', 'habeas corpus', 'ação', 'acao', 'pedido', 'requerimento',
  'substabelecimento', 'declaração', 'declaracao', 'certidão', 'certidao'
];

// Filtros de exclusão para evitar falsos positivos
const EXCLUSION_FILTERS = [
  'boa noite', 'bom dia', 'boa tarde', 'olá', 'oi', 'tchau', 'obrigado', 'obrigada',
  'por favor', 'desculpa', 'desculpe', 'como vai', 'tudo bem', 'beleza', 'valeu'
];

// Detecta se o conteúdo é um documento jurídico (MUITO MAIS RIGOROSO)
export function isDocumentContent(content: string): boolean {
  const normalizedContent = content.toLowerCase();
  
  // PRIMEIRA BARREIRA: Comprimento mínimo
  if (content.length < 50) return false;
  
  // SEGUNDA BARREIRA: Filtros de exclusão para saudações simples
  const containsExclusionFilter = EXCLUSION_FILTERS.some(filter => 
    normalizedContent.includes(filter)
  );
  if (containsExclusionFilter) return false;
  
  // TERCEIRA BARREIRA: Deve ter pelo menos 2 palavras-chave jurídicas
  const keywordCount = DOCUMENT_KEYWORDS.filter(keyword => 
    normalizedContent.includes(keyword)
  ).length;
  
  // QUARTA BARREIRA: Estrutura jurídica claramente definida
  const hasDocumentStructure = (
    content.length > 300 && // Mínimo de caracteres aumentado
    (content.includes('\n\n') || content.includes('§') || content.includes('Art.') || 
     content.includes('CONSIDERANDO') || content.includes('REQUER') || 
     content.includes('Excelentíssimo') || content.includes('MM. Juiz') ||
     content.includes('CONSIDERANDO QUE') || content.includes('POSTO ISSO'))
  );
  
  // REGRA FINAL: Precisa de pelo menos 2 palavras-chave OU estrutura clara + 1 palavra-chave
  return (keywordCount >= 2) || (hasDocumentStructure && keywordCount >= 1);
}

// Detecta intenção de criar documento baseado na mensagem do usuário (MUITO MAIS RIGOROSO)
export function detectDocumentIntent(message: string): boolean {
  const normalizedMessage = message.toLowerCase();
  
  // PRIMEIRA BARREIRA: Comprimento mínimo para evitar saudações
  if (message.length < 15) return false;
  
  // SEGUNDA BARREIRA: Filtros de exclusão para saudações
  const containsExclusionFilter = EXCLUSION_FILTERS.some(filter => 
    normalizedMessage.includes(filter)
  );
  if (containsExclusionFilter) return false;
  
  // TERCEIRA BARREIRA: Verbos de ação específicos para documentos
  const intentPhrases = [
    'faça uma', 'faça um', 'faca uma', 'faca um', 'monte uma', 'monte um',
    'crie uma', 'crie um', 'elabore uma', 'elabore um', 'redija uma', 'redija um',
    'prepare uma', 'prepare um', 'gere uma', 'gere um', 'escreva uma', 'escreva um',
    'preciso de uma', 'preciso de um', 'quero uma', 'quero um'
  ];
  
  // QUARTA BARREIRA: Deve ter verbo de ação + palavra-chave jurídica específica
  const hasIntent = intentPhrases.some(phrase => normalizedMessage.includes(phrase));
  const hasDocumentKeyword = DOCUMENT_KEYWORDS.some(keyword => 
    normalizedMessage.includes(keyword)
  );
  
  // REGRA FINAL: AMBOS devem estar presentes (mais rigoroso)
  return hasIntent && hasDocumentKeyword;
}

// Gera título inteligente baseado no conteúdo
export function generateDocumentTitle(content: string): string {
  const normalizedContent = content.toLowerCase();
  
  // Mapear frases específicas primeiro (mais específico = maior prioridade)
  const specificPhrases: Record<string, string> = {
    'contrato de locação': 'Contrato de Locação',
    'contrato de locacao': 'Contrato de Locação', 
    'contrato de aluguel': 'Contrato de Aluguel',
    'contrato de compra e venda': 'Contrato de Compra e Venda',
    'contrato de trabalho': 'Contrato de Trabalho',
    'contrato de prestação de serviços': 'Contrato de Prestação de Serviços',
    'contrato de prestacao de servicos': 'Contrato de Prestação de Serviços',
    'contrato de sociedade': 'Contrato de Sociedade',
    'contrato social': 'Contrato Social',
    'contrato de parceria': 'Contrato de Parceria',
    'petição inicial': 'Petição Inicial',
    'peticao inicial': 'Petição Inicial',
    'ação de cobrança': 'Ação de Cobrança',
    'acao de cobranca': 'Ação de Cobrança',
    'ação trabalhista': 'Ação Trabalhista',
    'pensão alimentícia': 'Pensão Alimentícia',
    'pensao alimenticia': 'Pensão Alimentícia',
    'divórcio consensual': 'Divórcio Consensual',
    'divorcio consensual': 'Divórcio Consensual'
  };
  
  // Buscar por frases específicas primeiro
  for (const [phrase, title] of Object.entries(specificPhrases)) {
    if (normalizedContent.includes(phrase)) {
      return title;
    }
  }
  
  // Mapear palavras-chave gerais (fallback)
  const titleMappings: Record<string, string> = {
    'petição': 'Petição Inicial',
    'peticao': 'Petição Inicial',
    'contrato': 'Contrato',
    'parecer': 'Parecer Jurídico',
    'recurso': 'Recurso',
    'contestação': 'Contestação',
    'contestacao': 'Contestação',
    'procuração': 'Procuração',
    'procuracao': 'Procuração',
    'acordo': 'Acordo',
    'memorial': 'Memorial',
    'defesa': 'Defesa',
    'alegações': 'Alegações Finais',
    'alegacoes': 'Alegações Finais',
    'mandado': 'Mandado',
    'notificação': 'Notificação',
    'notificacao': 'Notificação',
    'intimação': 'Intimação',
    'intimacao': 'Intimação',
    'apelação': 'Apelação',
    'apelacao': 'Apelação',
    'embargos': 'Embargos',
    'agravo': 'Agravo',
    'habeas corpus': 'Habeas Corpus',
    'ação': 'Ação',
    'acao': 'Ação',
    'requerimento': 'Requerimento'
  };
  
  // Procurar pela primeira palavra-chave encontrada
  for (const [keyword, title] of Object.entries(titleMappings)) {
    if (normalizedContent.includes(keyword)) {
      return title;
    }
  }
  
  // Se não encontrar, usar título genérico com data
  const now = new Date();
  const dateStr = now.toLocaleDateString('pt-BR');
  return `Documento - ${dateStr}`;
}

// Cria documento no banco e retorna o ID
export async function createDocumentFromChat(
  content: string, 
  userId: string
): Promise<string | null> {
  try {
    const title = generateDocumentTitle(content);
    
    const { data, error } = await supabase
      .from('documents')
      .insert({
        id: uuidv4(),
        title,
        content,
        type: 'DOCUMENTO_CHAT',
        user_id: userId
      })
      .select('id')
      .single();
    
    if (error) {
      console.error('Erro ao criar documento:', error);
      return null;
    }
    
    return data.id;
  } catch (error) {
    console.error('Erro ao criar documento:', error);
    return null;
  }
}

export function cleanMarkdownForDisplay(content: string): string {
  if (!content) return "";
  return content.replace(/^>\s?(.*)$/gm, "$1").trim(); // só remove citações markdown
}

export function ensureParagraphs(htmlOrText: string){
  const src = (htmlOrText ?? '').trim();
  if (!src) return '';
  // Se já parece HTML, não toca
  if (/<\/?[a-z][\s\S]*>/i.test(src)) return src;

  // Texto puro -> <p> ... </p> (sem estilos inline; ABNT vem do CSS)
  const norm = src.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return norm
    .split(/\n\s*\n/g)
    .map(b => b.trim() ? `<p>${b.replace(/\n/g, '<br>')}</p>` : '')
    .join('');
}

// Extrai título do conteúdo HTML de forma inteligente
export function extractTitleFromContent(content: string): string {
  if (!content || typeof content !== 'string') return '';
  
  console.log('🔍 Extraindo título do conteúdo:', content.substring(0, 200));
  
  // Lista de títulos genéricos para ignorar
  const genericTitles = [
    'TÍTULO DO DOCUMENTO',
    'DOCUMENTO', 
    'TITLE',
    'CONTRATO',
    'PETIÇÃO',
    'PROCURAÇÃO'
  ];
  
  // Buscar todos os H1 no HTML
  const h1Matches = content.match(/<h1[^>]*>(.*?)<\/h1>/gi);
  if (h1Matches) {
    for (const match of h1Matches) {
      const titleText = match.replace(/<[^>]*>/g, '').trim();
      console.log('📝 H1 encontrado:', titleText);
      
      // Verificar se não é um título genérico
      if (titleText && !genericTitles.includes(titleText.toUpperCase())) {
        console.log('✅ Título específico extraído:', titleText);
        return titleText;
      }
    }
  }
  
  // Buscar por texto em MAIÚSCULA centralizado (padrão de títulos)
  const centerMatch = content.match(/<p[^>]*style="[^"]*text-align:\s*center[^"]*"[^>]*>([^<]+)<\/p>/i);
  if (centerMatch && centerMatch[1]) {
    const centerText = centerMatch[1].trim();
    if (centerText.length > 10 && centerText === centerText.toUpperCase()) {
      console.log('✅ Título centralizado extraído:', centerText);
      return centerText;
    }
  }
  
  // Buscar por padrões típicos de contratos
  const contractPattern = /CONTRATO\s+DE\s+([A-ZÁÊÉÍÓÚ\s]+)/i;
  const contractMatch = content.match(contractPattern);
  if (contractMatch) {
    const fullTitle = contractMatch[0].trim();
    console.log('✅ Título de contrato extraído:', fullTitle);
    return fullTitle;
  }
  
  console.log('⚠️ Nenhum título específico encontrado, usando fallback');
  return '';
}

export function cleanContentForEditor(raw: string): string {
  if (!raw || typeof raw !== 'string') return '';

  // Remove aspas duplas do markdown
  const withoutQuotes = raw.replace(/^"|"$/g, '').trim();
  
  // Usar ensureParagraphs para normalizar sem estilos inline
  return ensureParagraphs(withoutQuotes);
}